/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2017 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* global document, window */
/* eslint import/prefer-default-export: 0 */
/* eslint-disable max-classes-per-file */
import {
  action,
  computed,
  observable,
} from 'mobx';

/**
 * @file
 * @description
 * Supports interaction with IMS authentication and authorization.
 */

/**
 * @classdesc
 * Private data for the AuthAPI
 * @class
 */
class PrivateData {
  /** true if the user has signed in */
  @observable isSignedIn = undefined;

  /** true if the user has started to sign out */
  @observable isSigningOut = undefined;

  /** Will be used as the redirect URL when the user is signed out. */
  @observable signOutRedirect = undefined;

  /**
   * @description
   * To set the flag for user sign in
   * @kind action
   * @param {boolean} newState - The state to be set to user sign flag
   */
  @action
  setSignedIn(newState) {
    this.isSignedIn = newState;
  }

  /**
   * @description
   * To set the flag that the user is signing out
   * @kind action
   * @param {boolean} newState - The state to be set to user signing out flag
   */
  @action
  setSigningOut(newState) {
    this.isSigningOut = newState;
  }

  /**
   * @description
   * Sets the redirect URL for when the user is signed out.
   * @kind action
   * @param {string} signOutRedirect - The redirect URL to set.
   */
  @action
  setSignOutRedirect(signOutRedirect) {
    this.signOutRedirect = signOutRedirect;
  }
}
const privateData = new PrivateData();

/**
 * @class
 */
class AuthAPI {
  constructor() {
    const self = this;
    this.setConfig();
    function ready() {
      privateData.setSignedIn(!self.ims ? undefined : !!self.ims.isSignedInUser());
    }
    const addIMSEventListener = () => {
      if (this.ims.version.lastIndexOf('v2-', 0) === 0) {
        document.addEventListener('dc.imslib.ready', ready);
      } else {
        document.addEventListener('imslib.ready', ready);
      }
    };
    // imslibReady is a special event to avoid hardcoding the value of
    if (this.ims && this.ims.isReady && this.ims.isReady()) ready();
    // adobeIMS.DomEvents.READY is "imslib.ready"
    else if (window.adobe_dc_sdk && window.adobe_dc_sdk.imsReady) {
      ready();
    } else if (this.ims) {
      addIMSEventListener();
    } else if (window.adobe_dc_sdk && window.adobe_dc_sdk.loaded) {
      window.adobe_dc_sdk.loaded.ims.then(() => addIMSEventListener());
    }
  }

  /**
   * @description
   * Configures auth provider
   * @method
   * @param {object} config - Object with required configuration
   * @public
   */
  setConfig(config) {
    privateData.config = config || {};
  }

  /**
   * @description
   * Configures auth provider
   * @method
   * @returns {object} config - Configuration object
   * @public
   */
  get config() {
    return privateData.config;
  }

  /**
   * @description
   * To get the IMS API implementation
   * @method
   * @returns {object} - Implementation of IMS API.
   * @public
   */
  get ims() {
    return window.adobeIMS;
  }

  /**
   * @description
   * Tells whether user is signed in.
   * @kind computed
   */
  @computed
  get isSignedIn() {
    return privateData.isSignedIn;
  }

  /**
   * @description
   * Tells whether user has started signing out.
   * @kind computed
   */
  @computed
  get isSigningOut() {
    return privateData.isSigningOut;
  }

  /**
   * @description
   * Gets the signOutRedirect that will be used when a user is signed out.
   * @method
   * @returns {String} - The URL to use for the redirect.
   * @public
   * @kind computed
   */
  @computed
  get signOutRedirect() {
    return privateData.signOutRedirect;
  }

  /**
   * @description
   * Sets the signOutRedirect that will be used when a user is signed out.
   * @method
   * @param {String} redirect - The URL to use for the redirect.
   * @public
   */
  set signOutRedirect(redirect) {
    privateData.signOutRedirect = redirect;
  }

  /**
   * @description
   * To check if we have an access token
   * @method
   * @returns {Boolean} - Whether we have an access token.
   * @public
   */
  hasAccessToken() {
    return this.getAccessTokenInfo().access_token !== '';
  }

  /**
   * @description
   * To get the user profile
   * @method
   * @returns {object} - User profile
   * @public
   */
  getUserProfile() {
    return (
      this.ims.getUserProfile() || {
        name: 'Anonymous',
      }
    );
  }

  /**
   * @description
   * To get the access token information
   * @method
   * @returns {object} - object with access token and related info.
   * - access_token: IMS access token
   * - expires_in: How long it's valid
   * - token_type: In our case, 'bearer'
   * @public
   */
  getAccessTokenInfo() {
    let tokenInformation;
    let expiresIn;
    let accessToken;
    let sid;

    if (this.ims) {
      if (this.ims.version.startsWith('v2-')) {
        tokenInformation = this.ims.getAccessToken();
        expiresIn = tokenInformation && tokenInformation.expire ? tokenInformation.expire - Date.now() : 0;
        accessToken = tokenInformation ? tokenInformation.token : '';
        sid = tokenInformation ? tokenInformation.sid : undefined;
      } else {
        tokenInformation = this.ims.getAccessTokenInfo();
        expiresIn = tokenInformation ? tokenInformation.expires_in : 0;
        accessToken = tokenInformation ? tokenInformation.access_token : '';
        sid = undefined;
      }
    }

    return {
      access_token: accessToken,
      expires_in: expiresIn,
      sid,
      token_type: 'bearer',
    };
  }

  /**
   * @description
   * To get the access token information.
   * Refresh token in case access token is expired.
   * @method
   * @returns Promise<{object}> - object with access token and related info.
   * - access_token: IMS access token
   * - expires_in: How long it's valid
   * - sid: Session identifier
   * - token_type: In our case, 'bearer'
   * @public
   */
  getAccessTokenInfoAsync = async () => {
    if (this.isSignedIn && !this.hasAccessToken()) {
      await this.refreshAccessToken();
    }
    return this.getAccessTokenInfo();
  };

  /**
   * @description
   * Method to satisfy the DC API IdentityAccess interface to get access
   * token and related info.
   * @method
   * @returns {Promise} -  Promise for an Object with following properties on success:
   *   status: 200, content: {
   *      access_token: (String) Access, or session, token
   *      token_type: (String) Usually "bearer"
   *      expires_in: (Number) # msecs until token expiration
   *   }
   * @public
   */
  async getSessionToken() {
    const tokenInfo = await this.getAccessTokenInfoAsync().catch(() => this.getAccessTokenInfo());
    return {
      status: 200,
      content: tokenInfo,
    };
  }

  /**
   * @description
   * Signs in user.
   * @method
   * @public
   *
   * @param {Object} customParams - Customize IMS sign-in options.
   * For example to provide login user hint pass customParams as {puser: <email>}
   */
  signIn(customParams) {
    privateData.setSigningOut(false);
    this.ims.signIn(customParams || {});
  }

  /**
   * @description
   * Signs out user, setting the isSigningOut observable true.
   * @method
   * @public
   *
   * @param {String} redirectUri - Where to go after signout.
   */
  signOut(redirectUri) {
    let accessToken; let xSignOutHost; let
      xSignOutUri;
    const customParams = {};
    const accessTokenInfo = this.getAccessTokenInfo();

    privateData.setSigningOut(true);
    if (redirectUri) {
      xSignOutHost = privateData.config.xSignOutHost;
      if (accessTokenInfo) {
        accessToken = accessTokenInfo.access_token;
      }
      // Tell IMS to redirect to xSignOutHost which will then redirect to redirectUri
      if (xSignOutHost && accessToken) {
        redirectUri = encodeURIComponent(redirectUri);
        xSignOutUri = `${xSignOutHost}/xsignout?redirect_uri=${redirectUri}&access_token=${accessToken}`;
        customParams.redirect_uri = xSignOutUri;
      } else {
        customParams.redirect_uri = redirectUri;
      }
    }
    this.ims.signOut(customParams);
  }

  /**
   * @description
   * This sets the isSigningOut observable for cases of a separate call to adobeIMS signOut(),
   * e.g. from the CC-DC utilnav.
   * @method
   * @public
   *
   * @param {Boolean} bool - The boolean value to set for the signing out state.
   */
  setSigningOut(bool) {
    privateData.setSigningOut(bool);
  }

  /**
   * @description
   * Toggles user's signed-in state.
   * @method
   * @param {String} [redirectUri] - The URI to be redirected to after we sign out the user. If not given, it will
   * default to the previously provided signOutRedirect (e.g. auth.signOutRedirect = "...").
   * @public
   */
  toggleSignedIn(redirectUri) {
    if (this.isSignedIn) {
      this.signOut(redirectUri || this.signOutRedirect);
    } else {
      this.signIn();
    }
  }

  /**
   * @description
   * Refreshes user's access token.
   * @method
   * @returns {Promise} Promise which resolves when refresh is complete
   * @public
   */
  refreshAccessToken() {
    if (this.ims.version.startsWith('v2-')) {
      return this.ims.refreshToken();
    }
    return new Promise((resolve, reject) => {
      if (this.isSignedIn) {
        this.ims.acquireAccessToken(
          () => {
            resolve();
          },
          err => {
            reject(err.stack || err.message);
          },
        );
      } else {
        reject(new ReferenceError('Not signed-in!'));
      }
    });
  }
}

const auth = new AuthAPI();
// inject this auth instance into the dc-core object so that it can be resolved by code such as:
// import { auth } from 'dc-core';
const core = window.adobe_dc_sdk.appLauncher.coreFunction();
core.auth = auth;

export { auth, AuthAPI };
